import { useStrictLocalization } from "@faire/web--source/common/localization";
import { backwardsCompatibleValues } from "@faire/web--source/common/localization/localize";
import { isPrerender } from "@faire/web--source/common/user-agent/isPrerender";
import { HorizontalLine } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Anchor as AnchorTag } from "@faire/web--source/ui/LinkAnchor";
import { WhenVisibilityIsImminent } from "@faire/web--source/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import React, { Suspense } from "react";
import styled from "styled-components";

import { IReviewSummary } from "@faire/retailer/lib/buildReviewSummary";
import { useTopOfPageContent } from "@faire/retailer/stores/ui/useTopOfPageContent";
import {
  BrandReviewsFallback,
  LazyBrandReviewsList,
} from "@faire/retailer/views/brand/BrandReview";

export const ReviewsSection: React.FC<{
  brand?: IProductPageResponse.IPdpBrand;
  product?: IProductPageResponse.IPdpProduct;
  productReviewSummary?: IReviewSummary;
  brandReviewSummary?: IReviewSummary;
}> = ({ brand, product, productReviewSummary, brandReviewSummary }) => {
  const { strictLocalize } = useStrictLocalization();
  const { headerHeight } = useTopOfPageContent();
  if (!brand || !product || !brandReviewSummary) {
    return null;
  }

  return (
    <>
      <HorizontalLine />
      <Anchor
        id="brand-reviews-summary"
        style={{
          top: `${-headerHeight}px`,
        }}
      />
      <WhenVisibilityIsImminent
        forceRender={isPrerender()}
        customizePlaceholder={<BrandReviewsFallback />}
      >
        <Suspense fallback={<BrandReviewsFallback />}>
          <SectionsWrapper>
            <Spacer height={{ mobileAndAbove: "3x", tabletAndAbove: "5x" }} />
            <LazyBrandReviewsList
              title={strictLocalize(
                {
                  defaultMessage:
                    "{reviewsCount, plural, one {# brand review} other {# brand reviews}}",
                  description: {
                    text: "number of reviews received by a brand",
                  },
                },
                backwardsCompatibleValues({
                  reviewsCount: brandReviewSummary?.numberOfReviews ?? 0,
                })
              )}
              brand={brand}
              product={product}
              brandReviewSummary={brandReviewSummary}
              productReviewSummary={productReviewSummary}
              brandToken={brand.token!}
              brandName={brand.name!}
              brandAvatar={brand.squared_image}
              productToken={product.token}
              containerName="product-page"
            />
            <Spacer
              height={{
                mobileAndAbove: "3x",
                tabletAndAbove: "5x",
              }}
            />
          </SectionsWrapper>
        </Suspense>
      </WhenVisibilityIsImminent>
    </>
  );
};

const Anchor = styled(AnchorTag)`
  display: block;
  position: relative;
  visibility: hidden;
  ${responsiveMediaQueries.mobile} {
    top: 0;
  }
`;

const SectionsWrapper = styled.div`
  padding: ${getSpacing(0, "2x")};
  ${responsiveMediaQueries.desktopAndAbove} {
    padding: ${getSpacing(0, "6x")};
  }
`;
